import { AddOnsList, AwaitingPaymentNotice, PageContainer, PageWithFooter, StepFooter, StepScroll } from './styles';
import { PartyCard, PartyHeader, PartyLockedTag, PartyName, UnresolvedNotice } from './components/styles';
import React, { useCallback, useMemo, useState } from 'react';

import { AddOnRow } from './components/add-on-row';
import { EstimateSummary } from './components/estimate-summary';
import { InfoCircle, Lock_01 } from '@hqo/react-components-library/dist/icons';
import { StepCta } from './components/step-cta';
import { AddOnQuestions } from './components/add-on-questions';
import { AddOnsOrdered } from './components/add-ons-ordered';
import { addOnReceipt } from 'components/ordered-add-ons/add-on-receipt';
import { AddOnPayment } from 'components/add-on-payment/add-on-payment';
import { Skeleton } from 'pages/room-layout/skeleton/room-layout-skeleton';
import { Spinner } from '@hqo/react-components-library/dist/atoms/spinner';
import { BookingAddOnsStep, useBookingAddOns } from './use-booking-add-ons.hook';
import { quantityViolationText } from './quantity-violation-text';
import { QuantityViolationNotice } from './components/quantity-violation-notice';
import { useFocusWhenShown } from './use-focus-when-shown.hook';
import { StepHeader, useSetStepHeader } from './step-header.context';
import { useIntl } from 'react-intl';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { AddOnCatalog } from './components/add-on-catalog';
import { AddOnsDialogFooter } from './dialog/add-ons-dialog-footer';
import { Info } from 'lucide-react';

/*
 * RES-1050 — the post-booking add-on step.
 *
 * ⛔ THIS IS NOT THE IN-CHECKOUT PAGE WITH A NEW ENTRY POINT. It runs after the booking exists, and its
 * selection is handed to the Service Request path rather than joining the room's cart — the cart resolves
 * exactly one payment recipient, so it cannot carry both the landlord's room money and a vendor's.
 *
 * ⛔ NO PAYMENT IS INVOKED HERE, and the amounts are ESTIMATES rather than charges: the vendor prices the
 * job afterwards, and payment happens on the tenant-facing Service Request surface.
 *
 * The content holds no opinion about being presented as a sheet or a flat page, so presentation can change
 * without touching this component.
 */
export const BookingAddOnsPage = (): JSX.Element => {
  const intl = useIntl();
  /* VIS-2193. The image-led add-on cards and a pinned footer, behind `show-product-images`. Off: as before. */
  const { showProductImages, enable2WayChatRedesignResourceBooking } = useFlags();
  const quantityNoticeRef = useFocusWhenShown<HTMLDivElement>();
  /*
   * RES-1407. Under the 2-way chat redesign a booking just made lives in My bookings, not on the old
   * receipt, so the confirmation's way on is "View booking" (`onViewMyBookings`, with it selected).
   */
  const showsBookingAfterOrder = Boolean(enable2WayChatRedesignResourceBooking);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const {
    booking,
    bookingStart,
    isLoading,
    selectedCount,
    partySections,
    selectedPartyKey,
    isPartyLocked,
    isKept,
    estimateTotal,
    estimateCurrency,
    hasUnpriceable,
    quantityOf,
    minQuantityOf,
    isAtMax,
    isFixedQuantity,
    isAwaitingRanges,
    onIncrease,
    onDecrease,
    onRemove,
    isSubmitting,
    isAwaitingPayment,
    hasSubmitFailed,
    rangeViolations,
    step,
    hasForm,
    isResolvingForm,
    formDefinition,
    formValues,
    isFormValid,
    unresolvedAddOns,
    hasInvalidAnswers,
    onFormChange,
    onFormValidate,
    onContinue,
    onBackToPicker,
    onBackToBooking,
    payment,
    onPaymentSettled,
    onPaymentCharged,
    orderedAddOns,
    onViewMyBookings,
  } = useBookingAddOns();

  /*
   * The same guarded path the booking card already takes. A charge is not evidence that the charge
   * describes this order: after an amend one exists, has been released, and states the superseded
   * total. The payment disposition is the discriminator, and this is what reads it.
   */
  const receipt = useMemo(() => addOnReceipt(payment.payment), [payment.payment]);

  const onToggle = useCallback((addOnId: string) => {
    setExpandedId(current => (current === addOnId ? null : addOnId));
  }, []);

  /*
   * RES-1336 — billing's payment sheet, which is a DETOUR from this flow rather than a screen in it.
   *
   * ⛔ IT IS NOT WRAPPED IN ANYTHING OF OURS, and the step machine knows nothing about it. The widget
   * is its own overlay — a ui-atoms Dialog on desktop, a Drawer on mobile, portaled to
   * `document.body` — so it already answers both form factors, and the screen we used to render
   * behind it could only ever be an empty surface carrying a second close control.
   *
   * ⚠️ Mounted inside the step's own tree purely so it unmounts with it; the portal means its
   * position here has no bearing on where it draws.
   *
   * RES-1367: ⛔ CLOSED UNTIL ASKED FOR. This opened itself the instant a session existed, so a
   * tenant who had just ordered was handed a card form over a confirmation they had not finished
   * reading -- and the one control on screen was a dismissal of something they never summoned. The
   * order stands whether or not it is paid here and the vendor can still bill, so the charge is
   * offered (`onPayNow` on the confirmation) rather than imposed.
   */
  const [isPaymentOpen, setIsPaymentOpen] = useState(false);

  const onPayNow = useCallback(() => setIsPaymentOpen(true), []);

  const onPaymentOpenChange = useCallback(
    (open: boolean) => {
      setIsPaymentOpen(open);

      /*
       * ⛔ FORWARD ON DISMISS. The add-ons were written before the sheet opened; only the charge is
       * outstanding, so closing it lands on the confirmation rather than back on a screen that
       * implies the order can still be edited.
       */
      if (!open) {
        onPaymentSettled();
      }
    },
    [onPaymentSettled],
  );

  /*
   * ⛔ `onPaymentComplete` DOES NOT CLOSE THE SHEET, and that distinction is the whole of it. The
   * widget fires it on SUCCESS ONLY — a decline is reported through the checkout session's own
   * status and never reaches it — and then renders its own `PaymentSuccess` inside the still-open
   * overlay. An earlier revision closed from here and tore that screen away the instant it appeared,
   * landing a tenant who HAD paid on a confirmation that never said so. The callback was then
   * dropped altogether, which went too far the other way: it is the only signal this app gets that a
   * charge was made, and the confirmation needs it to stop calling the figure an estimate.
   *
   * ⚠️ `onOpenChange(false)` remains the SINGLE exit, and the same one for every ending: paid and
   * dismissed, declined and dismissed, expired, or abandoned outright. There is no success-only path
   * out — the order stands in all four and only the charge differs.
   *
   * ⚠️ ONE ENDING NEVER COMES BACK HERE AT ALL. A gateway needing 3-D Secure returns a `checkoutUrl`
   * and the widget sends the whole window to it; the return is a fresh mount with no
   * `payableBookingId`, and eligibility bounces the booking besides — `!details?.add_ons?.length` is
   * false once the order exists. So that tenant does not reach this screen, paid or unpaid, and
   * nothing here mitigates it. Closing that gap means persisting the order's id, which is its own
   * ticket rather than a comment.
   */
  const paymentSheet = payment.checkoutSessionUuid ? (
    <AddOnPayment
      checkoutSessionUuid={payment.checkoutSessionUuid}
      open={isPaymentOpen}
      onOpenChange={onPaymentOpenChange}
      onPaymentComplete={onPaymentCharged}
    />
  ) : null;

  /*
   * RES-1336 — the header the wrapper should draw for the step that is on screen. Declared here, ABOVE
   * the early returns below, because hooks cannot be called conditionally; the step alone decides it,
   * so the loading and empty guards reuse the picker's, which is correct — they ARE the picker, waiting.
   *
   * Only the middle screen names a back target. The first and last exit the flow rather than going
   * back, and both exit to the booking the tenant came from rather than closing the surface outright:
   * reaching the add-ons meant passing through the confirmation, so that is where "away from here" is.
   */
  /*
   * A lookup rather than a chain of ternaries: four screens now, and each arm carries the reasoning
   * for its own state, which a nested conditional buries.
   */
  const STEP_HEADERS: Record<BookingAddOnsStep, StepHeader> = {
    /* The two working screens go BACK, never close: an X here read as "close" and left the tenant no
       way to the receipt at all, which is what made this surface feel like a dead end. */
    picker: { title: intl.formatMessage({ id: 'add_ons' }), onBack: onBackToBooking, withDesktopModal: true },
    /* RES-1357: an unpriced line makes this screen a quote request, and the header says so. */
    questions: {
      title: intl.formatMessage({ id: hasUnpriceable ? 'add_on_quote_title' : 'checkout' }),
      onBack: onBackToPicker,
      withDesktopModal: true,
    },
    /* The order is written before this renders, so there is nothing upstream to edit. */
    confirmation: {
      /* ⚠️ Named, not painted: `AddOnsOrdered` renders this same id as its focus-managed heading. */
      title: intl.formatMessage({ id: 'add_on_ordered_title' }),
      titleOwnedByContent: true,
      /* RES-1407: under the redesign, closing goes where "View booking" does; there is no receipt. */
      onClose: showsBookingAfterOrder ? onViewMyBookings : onBackToBooking,
      withDesktopModal: true,
    },
  };

  useSetStepHeader(STEP_HEADERS[step]);

  /*
   * RES-1336 — checked beside the confirmation and for the same reason: placing the order re-reads the
   * booking, so by the time either renders the booking is ineligible and `isLoading` may be true again
   * from that read. Both guards below would then hide a screen the tenant has already earned.
   */
  if (step === 'confirmation' && orderedAddOns) {
    return (
      <PageContainer data-testid="booking-add-ons-page">
        <AddOnsOrdered
          order={orderedAddOns}
          charge={receipt.charge}
          isAwaitingPrice={receipt.isAwaitingPrice}
          receiptTotal={receipt.total}
          onViewMyBookings={onViewMyBookings}
          onBackToBooking={onBackToBooking}
          onViewBooking={showsBookingAfterOrder ? onViewMyBookings : undefined}
          /*
           * Nothing to offer once it is paid, nor on an order that never had a charge to make. Read
           * off the receipt, not the raw block: after an amend the old charge is still present and
           * would hide the button on an order that now owes money.
           */
          onPayNow={payment.checkoutSessionUuid && !receipt.charge ? onPayNow : undefined}
        />
        {/*
          ⛔ THE ONLY PLACE THE WAIT CAN BE SHOWN, now that every order lands here. Between the write
          and the sheet are three asynchronous hops — the selection is written, a consumer creates the
          linked request, billing opens a session on it — and without a word for them the confirmation
          simply sits there and a payment sheet arrives unannounced a few seconds later.

          ⚠️ It says the charge is COMING, not that anything is unfinished: the add-ons ARE ordered by
          the time this renders, and `AddOnsOrdered` above has already said so. If the wait runs out
          the hook settles and this disappears, leaving the confirmation exactly as it would have been.
        */}
        {isAwaitingPayment && (
          <AwaitingPaymentNotice role="status" aria-live="polite" data-testid="add-on-awaiting-payment">
            <Spinner size="16px" color="currentColor" />
            <span>{intl.formatMessage({ id: 'add_on_payment_preparing' })}</span>
          </AwaitingPaymentNotice>
        )}
        {paymentSheet}
      </PageContainer>
    );
  }

  if (isLoading) {
    return <Skeleton />;
  }

  /*
   * No resolved booking, no page. The bounce is driven from the hook; this stops a frame rendering
   * against a booking that does not exist.
   */
  if (!booking) {
    return null;
  }

  const hasSelection = selectedCount > 0;
  const selectedParty = partySections.find(section => section.key === selectedPartyKey);

  /*
   * ⭐ The questions are a separate SCREEN, switched locally. `hasForm` is re-checked here as well as
   * in the hook's own effect: the form can vanish underneath this render — the tenant clears their
   * last add-on, or the resolve fails open — and rendering `AddOnQuestions` with a null definition
   * would crash a tenant who has already paid.
   */
  if (step === 'questions' && hasForm && formDefinition) {
    return (
      <PageContainer data-testid="booking-add-ons-page">
        <AddOnQuestions
          definition={formDefinition}
          values={formValues}
          partyName={selectedParty?.name}
          partyLogoUrl={selectedParty?.logoUrl}
          onChange={onFormChange}
          onValidate={onFormValidate}
          hasUnpriceable={hasUnpriceable}
        />
        {/*
          RES-1334. Belongs on this screen, not the picker: the picker's CTA never submits while a
          form exists, so a notice there is unreachable while the tenant taps a dead button here.
          Blocks rather than warns — unlike `unresolvedAddOns`, this one the tenant can clear.
          Names no field: labels are rich text this app cannot flatten, and the renderer has already
          marked each one inline. `role="alert"` because it gates the CTA.
        */}
        {hasInvalidAnswers && (
          <UnresolvedNotice role="alert" data-testid="booking-add-ons-invalid-answers-notice">
            <InfoCircle size="sm" variant="adminGreyDark" aria-hidden="true" />
            <span>{intl.formatMessage({ id: 'add_on_answers_invalid' })}</span>
          </UnresolvedNotice>
        )}
        <StepCta
          hasSelection={hasSelection}
          estimateTotal={estimateTotal}
          estimateCurrency={estimateCurrency}
          hasUnpriceable={hasUnpriceable}
          isSubmitting={isSubmitting}
          hasSubmitFailed={hasSubmitFailed}
          onContinue={onContinue}
          step={step}
          commits={step === 'questions' || !hasForm}
          /*
           * ⛔ The validity gate. Also re-checked inside `onSubmit` — see the note on `isDisabled`.
           *
           * RES-1334: `isFormValid` alone is not enough. The renderer normalises `"a"` to `null`
           * before validating, so it reports valid and this button stayed enabled.
           */
          isDisabled={!isFormValid || hasInvalidAnswers}
        />
        {paymentSheet}
      </PageContainer>
    );
  }

  /*
   * VIS-2193, behind `show-product-images`: the image-led cards, with the estimate and the CTA pinned in
   * a footer below the menu (the same `AddOnsDialogFooter` the My bookings dialog uses), instead of the
   * total sitting above the menu and the CTA scrolling away under it. Flag off: the layout below.
   */
  if (showProductImages) {
    return (
      <PageWithFooter data-testid="booking-add-ons-page">
        <StepScroll data-testid="booking-add-ons-scroll">
          <AddOnCatalog
            partySections={partySections}
            isPartyLocked={isPartyLocked}
            isKept={isKept}
            quantityOf={quantityOf}
            minQuantityOf={minQuantityOf}
            isAtMax={isAtMax}
            isFixedQuantity={isFixedQuantity}
            isAwaitingRanges={isAwaitingRanges}
            onIncrease={onIncrease}
            onDecrease={onDecrease}
            onRemove={onRemove}
            expandedId={expandedId}
            onToggle={onToggle}
            bookingStart={bookingStart}
            headingLevel="h2"
            testIdPrefix="booking-add-ons-catalog"
          />
          {/* Warn, do not block: see the same notice on the layout below. */}
          {unresolvedAddOns.length > 0 && (
            <p
              role="status"
              className="mt-6 mb-0 flex items-start gap-2 rounded-lg bg-muted px-3 py-2 text-sm text-muted-foreground"
              data-testid="booking-add-ons-unresolved-notice"
            >
              <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
              <span>
                {intl.formatMessage(
                  { id: 'add_on_unavailable_here' },
                  { items: unresolvedAddOns.map(addOn => addOn.name).join(', ') },
                )}
              </span>
            </p>
          )}
          <QuantityViolationNotice
            violations={rangeViolations}
            className="mt-6"
            testId="booking-add-ons-quantity-notice-images"
          />
        </StepScroll>
        <StepFooter>
          <AddOnsDialogFooter
            hasSelection={hasSelection}
            estimateTotal={estimateTotal}
            estimateCurrency={estimateCurrency}
            hasUnpriceable={hasUnpriceable}
            isSubmitting={isSubmitting}
            hasSubmitFailed={hasSubmitFailed}
            onContinue={onContinue}
            step={step}
            commits={step === 'questions' || !hasForm}
            /* Not the same as "no form": see the CTA below. */
            isDisabled={isResolvingForm || rangeViolations.length > 0}
          />
        </StepFooter>
        {paymentSheet}
      </PageWithFooter>
    );
  }

  return (
    <PageContainer data-testid="booking-add-ons-page">
      {/*
        No heading here. The modal header above already says "Add-ons", and this said "Add to your
        booking" directly beneath it — two titles stacked on one screen, where the design has one. The
        dialog carries its name for a screen reader (`aria-label` on desktop, the sheet's own title on
        mobile), and each provider block below is still a heading, so the screen keeps its structure.
      */}
      <EstimateSummary
        hasSelection={hasSelection}
        estimateTotal={estimateTotal}
        estimateCurrency={estimateCurrency}
        hasUnpriceable={hasUnpriceable}
      />
      <AddOnsList data-testid="booking-add-ons-list">
        {partySections.map(section => {
          const isLocked = isPartyLocked(section.key);
          const headingId = `booking-add-on-party-${section.key || 'unattributed'}`;

          return (
            /*
             * A `role="group"` labelled by its own heading, so a locked row is announced with the party it
             * belongs to. `aria-labelledby` only when there IS a heading -- a dangling reference is worse than
             * none. Never a <ul>/<li>: `aria-disabled` on a listitem is an eslint error in this config.
             */
            <PartyCard
              key={section.key}
              role="group"
              data-testid={`booking-add-on-party-${section.key || 'unattributed'}`}
              /*
               * Named only when there IS a heading: the "Add-ons" fallback collides verbatim with the summary
               * card's own "Add-ons" line, and an unnamed group beats a misleadingly-named one.
               */
              aria-labelledby={section.name ? headingId : undefined}
            >
              {section.name && (
                <PartyHeader as="h2">
                  <PartyName id={headingId}>{section.name}</PartyName>
                  {isLocked && (
                    <PartyLockedTag data-testid={`booking-add-on-party-locked-${section.key}`}>
                      <Lock_01 size="xs" variant="adminGreyDark" aria-hidden="true" />
                      {intl.formatMessage({ id: 'add_on_party_locked' })}
                    </PartyLockedTag>
                  )}
                </PartyHeader>
              )}
              {section.addOns.map(addOn => (
                <AddOnRow
                  key={addOn.id}
                  addOn={addOn}
                  quantity={quantityOf(addOn.id)}
                  onIncrease={onIncrease}
                  onDecrease={onDecrease}
                  onRemove={onRemove}
                  isExpanded={expandedId === addOn.id}
                  onToggle={onToggle}
                  isPartyLocked={isLocked}
                  isKept={isKept(addOn)}
                  isFixedQuantity={isFixedQuantity(addOn.id)}
                  isAtMax={isAtMax(addOn.id)}
                  isAwaitingRanges={isAwaitingRanges}
                  minQuantity={minQuantityOf(addOn.id)}
                  bookingStart={bookingStart}
                />
              ))}
            </PartyCard>
          );
        })}
      </AddOnsList>
      {/*
        Deliverable 6. An item returned in NO owner's group has an owner with no instance at this
        building — exactly what the Service Request create rejects on. ⚠️ WARN, DO NOT BLOCK: the
        tenant has already paid for the room, and the fix is an admin's to make, so blocking submit
        would strand them behind a condition they cannot clear. Naming the item lets them remove it.
      */}
      {unresolvedAddOns.length > 0 && (
        <UnresolvedNotice role="status" data-testid="booking-add-ons-unresolved-notice">
          <InfoCircle size="sm" variant="adminGreyDark" aria-hidden="true" />
          <span>
            {intl.formatMessage(
              { id: 'add_on_unavailable_here' },
              { items: unresolvedAddOns.map(addOn => addOn.name).join(', ') },
            )}
          </span>
        </UnresolvedNotice>
      )}
      {rangeViolations.length > 0 && (
        <UnresolvedNotice
          ref={quantityNoticeRef}
          tabIndex={-1}
          role="alert"
          data-testid="booking-add-ons-quantity-notice"
        >
          <InfoCircle size="sm" variant="adminGreyDark" aria-hidden="true" />
          <span>{quantityViolationText(intl, rangeViolations)}</span>
        </UnresolvedNotice>
      )}
      <StepCta
        hasSelection={hasSelection}
        estimateTotal={estimateTotal}
        estimateCurrency={estimateCurrency}
        hasUnpriceable={hasUnpriceable}
        isSubmitting={isSubmitting}
        hasSubmitFailed={hasSubmitFailed}
        onContinue={onContinue}
        step={step}
        commits={step === 'questions' || !hasForm}
        /*
         * ⛔ Not the same as "no form". A resolve still running has not answered yet, and submitting
         * through it sends no answers to a vendor who did ask. The handler ignores the tap too.
         */
        isDisabled={isResolvingForm || rangeViolations.length > 0}
      />
      {paymentSheet}
    </PageContainer>
  );
};

export default BookingAddOnsPage;
